import React, { useState, useContext, useEffect } from "react";
import { Modal } from "@mui/material";
import styled from "styled-components";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import { Badge } from "../../Networking/Interface/Account/AccountInterface";
import { ToastType } from "../../Components/Toast/Toast";
import { useToast } from "../../Components/Toast/ToastContext";
import { Button } from "antd";

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 450px;
  max-height: 90vh;
  overflow: auto;
  background-color: rgba(255, 255, 255, 0.96);
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const Title = styled.h3`
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  color: #333;
`;

const BadgeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

const BadgeItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid ${(props) => (props.selected ? "orange" : "#e0e0e0")};
  background: ${(props) => (props.selected ? "rgba(255, 165, 0, 0.08)" : "#fafafa")};
  transition: all 0.2s ease;

  &:hover {
    border-color: orange;
    background: rgba(255, 165, 0, 0.05);
  }
`;

const BadgeInfo = styled.div`
  flex: 1;
`;

const BadgeName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const BadgeDesc = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 2px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

interface AwardBadgeProps {
  userId: number;
  onSuccess?: () => void;
}

const AwardBadge: React.FC<AwardBadgeProps> = ({ userId, onSuccess }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [selectedBadgeId, setSelectedBadgeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const api = useContext(SunsetAPIAccountContext);
  const { showToast } = useToast();

  useEffect(() => {
    if (!modalOpen || !api) return;
    const fetchBadges = async () => {
      const response = await api.getAllBadges();
      if (response.success && response.data) {
        setBadges(response.data);
      }
    };
    fetchBadges();
  }, [modalOpen, api]);

  const handleAward = async () => {
    if (selectedBadgeId === null) {
      showToast(ToastType.Error, "Select a badge first", 5);
      return;
    }

    setLoading(true);
    try {
      const response = await api?.awardBadge(userId, selectedBadgeId);

      if (response?.code === 409) {
        showToast(ToastType.Error, "User already has this badge", 5);
        setLoading(false);
        return;
      }

      if (!response?.success) {
        throw new Error("Failed to award badge");
      }

      showToast(ToastType.Success, "Badge awarded successfully", 5);
      setModalOpen(false);
      setSelectedBadgeId(null);
      if (onSuccess) onSuccess();
    } catch (error) {
      showToast(ToastType.Error, "Failed to award badge", 5);
    }
    setLoading(false);
  };

  return (
    <div>
      <Button style={{ border: "none" }} onClick={() => setModalOpen(true)}>
        Award Badge
      </Button>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalContent>
          <Title>Award Badge</Title>
          {badges.length === 0 ? (
            <p style={{ textAlign: "center", color: "#999" }}>Loading badges...</p>
          ) : (
            <BadgeList>
              {badges.map((badge) => (
                <BadgeItem
                  key={badge.id}
                  selected={selectedBadgeId === badge.id}
                  onClick={() => setSelectedBadgeId(badge.id)}
                >
                  <BadgeInfo>
                    <BadgeName>★ {badge.name}</BadgeName>
                    <BadgeDesc>{badge.description}</BadgeDesc>
                  </BadgeInfo>
                </BadgeItem>
              ))}
            </BadgeList>
          )}
          <ButtonRow>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              style={{ backgroundColor: "orange", borderColor: "orange" }}
              onClick={handleAward}
              disabled={selectedBadgeId === null || loading}
            >
              {loading ? "Awarding..." : "Award"}
            </Button>
          </ButtonRow>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AwardBadge;
